<template>
  <div class="p-4 client-vitals">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Vitals</h5>
      <div v-if="!isEMAREnabled">
        <b-button
          class="pl-3 pr-3 pt-1 pb-1 mr-2"
          variant="primary"
          size="sm"
          @click="addVital"
          ><i class="fa fa-plus mr-2"></i>New</b-button
        >
        <b-button
          class="pl-3 pr-3 pt-1 pb-1 "
          variant="primary"
          size="sm"
          @click="exportExcel"
          ><i class="fa fa-table mr-2"></i>Export</b-button
        >
      </div>
      <div v-else class="d-flex flex-row justify-content-between gap-2 align-items-center emar-vitals-menu">
        <b-form-group :state="formErrors.datetimeFromEmar.length == 0 ? null : false" :invalid-feedback="formErrors.datetimeFromEmar.join(', ')">
            <template v-slot:label>
              Datetime from
              <span class="text-danger">*</span>
            </template>
            <date-time-picker v-model="datetimeFromEmar" />
          </b-form-group>
          <b-form-group :state="formErrors.datetimeToEmar.length == 0 ? null : false" :invalid-feedback="formErrors.datetimeToEmar.join(', ')">
            <template v-slot:label>
              Datetime to
              <span class="text-danger">*</span>
            </template>
            <date-time-picker v-model="datetimeToEmar" />
          </b-form-group>
      </div>
    </div>
    <grid
      v-if="client && eoc && ready"
      class=""
      :data-over-column="overVitalSignId"
      ref="grid"
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="vitalSigns"
      :columnLines="false"
      :searchFeature="true"
      @paint="listeners.paint"
      @mouseover="listeners.mouseOver"
      @mouseout="listeners.mouseOut"
      @renderrows="listeners.renderRows"
      :stripeFeature="false"
      :excelExporterFeature="excelExporterFeature"
    ></grid>
    <b-modal
      centered
      ref="form-modal"
      :title="formId ? 'Edit Vital' : 'Add Vital'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitVital"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-form-group label="Effective Date">
        <date-time-picker v-model="form.effectiveDate" />
      </b-form-group>
      <b-form-group
        v-for="vitalSign in vitalSignsNonAuto"
        :key="vitalSign.id"
        :label="vitalSign.name + (vitalSign.unit ? ` (${vitalSign.unit})` : '')"
      >
        <b-form-input
          v-if="vitalSign.type == 'text'"
          v-model="form.items[vitalSign.id]"
          trim
        ></b-form-input>
        <b-form-input
          v-if="vitalSign.type == 'number'"
          v-model="form.items[vitalSign.id]"
          @mousewheel="disableNumberInputScroll"
          type="number"
          trim
        ></b-form-input>
        <b-form-select
          v-if="vitalSign.type == 'singleSelection'"
          v-model="form.items[vitalSign.id]"
          :options="vitalSign.data ? JSON.parse(vitalSign.data) : []"
        ></b-form-select>
        <div class="d-flex flex-row justify-content-start">
          <b-form-input
            v-if="vitalSign.type == 'numberPair'"
            :value="numberPairValue(0, vitalSign.id)"
            @input="setNumberPair(0, vitalSign.id, $event)"
            type="number"
            @mousewheel="disableNumberInputScroll"
            trim
          ></b-form-input>
          <b-form-input
            v-if="vitalSign.type == 'numberPair'"
            :value="numberPairValue(1, vitalSign.id)"
            @input="setNumberPair(1, vitalSign.id, $event)"
            type="number"
            @mousewheel="disableNumberInputScroll"
            trim
            class="ml-2"
          ></b-form-input>
        </div>
      </b-form-group>
      <template v-if="bmiEnabled">
        <hr />
        <span v-if="bmiError" class="d-block text-danger mb-2">
          To calculate BMI, IBW and %IBW, Height (ft, in) and Weight (lbs) must
          be enabled
        </span>
        <b-form-group label="BMI">
          <b-form-input
            v-model="form.items[bmiVitalSign.id]"
            type="number"
            @mousewheel="disableNumberInputScroll"
            readonly
          />
        </b-form-group>
        <b-form-group label="Gender">
          <b-form-radio-group v-model="ibw.gender" name="gender">
            <b-form-radio value="f" class="mr-3">Female</b-form-radio>
            <b-form-radio value="m">Male</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group label="IBW">
          <b-form-input
            v-model="form.items[ibwVitalSign.id]"
            type="number"
            @mousewheel="disableNumberInputScroll"
            readonly
          />
        </b-form-group>
        <b-form-group label="IBW%">
          <b-form-input
            v-model="form.items[ibwPercentVitalSign.id]"
            type="number"
            @mousewheel="disableNumberInputScroll"
            readonly
          />
        </b-form-group>
      </template>
    </b-modal>
    <b-modal
      centered
      ref="view-modal"
      :title="viewVitalSign ? viewVitalSign.name : ''"
      header-bg-variant="primary"
      header-text-variant="white"
      @show="showVitalSignChart"
      size="lg"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer=""
        ><b-button
          class="ml-2"
          variant="primary"
          @click="$refs['view-modal'].hide()"
          >Ok</b-button
        >
      </template>
      <div class="vital-view-container">
        <b-row>
          <b-col v-if="viewVitalSign.type == 'number'">
            <chart
              :data="viewVitalSignChartData"
              :options="lineChartOptions"
              :style="lineChartStyles"
              type="line"
            />
          </b-col>
          <b-col>
            <grid
              class=""
              ref="view-grid"
              :groupFeature="false"
              :columns="viewColumns"
              :autoHeight="true"
              :data="
                viewVitalSign.values
                  ? viewVitalSign.values.filter(value => value.value)
                  : []
              "
              :columnLines="false"
              :searchFeature="true"
              :stripeFeature="false"
            ></grid>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Peity from "vue-peity/src/lib/peity";
import zipcelx from "zipcelx";
import Grid from "../../third_party/grid/Grid.vue";
import Chart from "../controls/Chart.vue";
import catalogServer from "../../server/catalog";
import vitalRecordServer from "../../server/vitalRecord";
import eocServer from "../../server/eoc";
import patientServer from "../../server/patient";
import moment from "moment";
import {
  formatDate,
  formatDateOnly,
  formatDateTimeMysql,
  formatDateTimeSql,
  formatDateISO
} from "../../helpers/date";
import emarServer from "../../server/emar";
import { addClass, removeClass, hasParentClass } from "../../helpers/layout";
import DateTimePicker from "../controls/DateTimePicker.vue";

export default {
  name: "EocViewVitals",
  components: {
    Grid,
    Chart,
    DateTimePicker
  },
  props: ["eocId", "clientId", "showing"],
  data() {
    const self = this;
    return {
      list: [],
      formId: null,
      form: {
        episodeOfCareId: this.eocId,
        items: {},
        effectiveDate: null
      },
      formErrors: {
        effectiveDate: [],
        datetimeFromEmar: [],
        datetimeToEmar: []
      },
      ibw: {
        gender: null
      },
      viewVitalSign: {},
      viewVitalSignChartData: null,
      overVitalSignId: null,
      lineChartOptions: {
        cubicInterpolationMode: "monotone"
      },
      lineChartStyles: {
        height: 100
      },
      listeners: {
        paint: paintEvent => {
          if (paintEvent.firstPaint) {
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "img.edit-vital",
              click: async event => {
                event.stopPropagation();
                this.formId = event.srcElement.dataset.id;
                const form = await vitalRecordServer.get(this.formId);
                const items = {};
                for (let index = 0; index < form.items.length; index++) {
                  const item = form.items[index];
                  items[item.vitalSign.id] = item.value;
                }
                this.form = {
                  episodeOfCareId: this.eocId,
                  items: items,
                  effectiveDate: formatDateTimeMysql(
                    form.effectiveDate ?? this.getCurrentAdjustedDateTime()
                  )
                };
                this.ibw = { gender: null };
                this.$refs["form-modal"].show();
              },
              capture: true,
              thisObj: this
            });
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "img.delete-vital",
              click: async event => {
                event.stopPropagation();
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await vitalRecordServer.delete(event.srcElement.dataset.id);
                    this.getVitals();
                    this.showAlertSuccess();
                  })
                  .catch(function() {});
              },
              capture: true,
              thisObj: this
            });
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "img.view-vital",
              click: async event => {
                event.stopPropagation();
                const vitalSignId = event.srcElement.dataset.id;
                const vitalSign = this.vitalSigns.find(
                  vitalSign => vitalSign.id == vitalSignId
                );
                const values = [];
                for (let index = 0; index < this.list.length; index++) {
                  const item = this.list[index];
                  const value = item.items.find(
                    val => val.vitalSign.id == vitalSignId
                  );
                  values.push({
                    dateTime: item.effectiveDate,
                    value: value ? value.value : ""
                  });
                }
                this.viewVitalSign = {
                  ...vitalSign,
                  values
                };
                this.$refs["view-modal"].show();
              },
              capture: true,
              thisObj: this
            });
          }
        },
        mouseOver(mouseEvent) {
          let headerContainer = mouseEvent.event.fromElement.querySelector(
            ".b-grid-header"
          );
          if (!headerContainer)
            headerContainer = hasParentClass(
              mouseEvent.event.fromElement,
              "b-grid-header"
            );
          document
            .querySelectorAll(`.grid-selected-column`)
            .forEach(elem => removeClass(elem, "grid-selected-column"));
          if (
            headerContainer &&
            headerContainer.dataset &&
            headerContainer.dataset.column != "vitalSign"
          ) {
            const columnItems = document.querySelectorAll(
              `[data-column="${headerContainer.dataset.column}"]`
            );
            columnItems.forEach(elem => addClass(elem, "grid-selected-column"));
          }
        },
        mouseOut() {},
        renderRows() {
          for (let index = 0; index < self.vitalSigns.length; index++) {
            const vitalSign = self.vitalSigns[index];
            const chartData = [];
            let min = 1000000;
            let max = -1000000;
            for (let index2 = 0; index2 < self.list.length; index2++) {
              const itemValue = self.list[index2];
              const vitalSignItem = itemValue.items.find(
                itemV => itemV.vitalSign.id == vitalSign.id
              );
              if (vitalSignItem && vitalSignItem.value) {
                const vitalSignItemValue = parseFloat(vitalSignItem.value);
                if (vitalSignItemValue || vitalSignItemValue >= 0) {
                  if (vitalSignItemValue > max) max = vitalSignItemValue;
                  if (vitalSignItemValue < min) min = vitalSignItemValue;
                  chartData.push(vitalSignItemValue);
                }
              }
            }

            if (vitalSign.type == "number") {
              const targetEl = self.$el.querySelector(
                "#vitalSign" + vitalSign.id
              );
              if (!targetEl) {
                continue;
              }

              targetEl.innerHTML = "";
              const parent = targetEl.parentNode;
              if (parent && parent.querySelectorAll) {
                parent.querySelectorAll("svg").forEach(svg => svg.remove());
              }

              const chart = new Peity(
                targetEl,
                "line",
                chartData.join(","),
                {
                  fill: "none",
                  stroke: "#aedd81",
                  strokeWidth: 2,
                  width: 100,
                  height: 30,
                  min,
                  max
                }
              );
              chart.draw();
            }
          }
        }
      },
      client: null,
      eoc: null,
      ready: false,
      currentView: "current",
      currentVitalSignsIds: [],
      viewOptions: [
        {
          value: "current",
          label: "Current EOC",
          icon: ""
        },
        {
          value: "fullHistory",
          label: "Full History",
          icon: ""
        }
      ],
      datetimeFromEmar: (() => {
        const date = new Date();
        date.setDate(date.getDate() - 30); // Default to 30 days ago
        date.setHours(0, 0, 0, 0);
        return formatDateTimeMysql(date);
      })(),
      datetimeToEmar: (() => {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30); // 30 days in the future
        futureDate.setHours(23, 59, 59, 999);
        return formatDateTimeMysql(futureDate);
      })(),
      lastTimeout: null
    };
  },
  methods: {
    disableNumberInputScroll(event) {
      event.target.blur();
    },
    ...mapActions("layout", ["showAlertSuccess", "showAlertError"]),
    ...mapActions("client", ["fetchVitalSigns"]),
    async fetchData() {
      await this.getEoc();
      await this.getClient();
      if (this.selectedLocation === null) {
        await this.getVitalsColumnsForEoCLocation();
      }
      if (this.vitalSigns.length === 0) {
        await this.fetchVitalSigns();
      }
      this.getVitals();
    },
    async getEoc() {
      this.eoc = await eocServer.get(this.eocId);
    },
    async getClient() {
      this.client = await patientServer.get(this.clientId);
    },
    async getVitals() {
      this.ready = false;
      const fromISO = formatDateISO(this.datetimeFromEmar);
      const toISO = formatDateISO(this.datetimeToEmar);
      const fromsql= moment(this.datetimeFromEmar, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD[T]HH:mm:ss");
      const tosql = moment(this.datetimeToEmar, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD[T]HH:mm:ss");
      if (this.isEMAREnabled){
        this.list = await vitalRecordServer.query(
          this.currentView == "fullHistory",
          this.currentView == "fullHistory" ? this.clientId : this.eocId, fromsql, tosql
        );
      }
      else{
          this.list = await vitalRecordServer.query(
          this.currentView == "fullHistory",
          this.currentView == "fullHistory" ? this.clientId : this.eocId
        );
      }
      setTimeout(() => {
        if (this.$el && this.$el.querySelectorAll) {
          this.$el.querySelectorAll(".b-virtual-scroller").forEach(scrollElem => {
            scrollElem.scrollLeft = 1000000;
          });
        }
      }, 500);
      this.ready = true;
      if (this.isEMAREnabled) {
        emarServer.query(this.eocId, fromISO, toISO)
          .then((response) => {
            const data = Array.isArray(response) ? response : (response.data || []);
            this.list = this.list.concat(data);
            this.list.sort((a,b)=>a.effectiveDate.localeCompare(b.effectiveDate));
            setTimeout(() => {
              if (this.$el && this.$el.querySelectorAll) {
                this.$el.querySelectorAll(".b-virtual-scroller").forEach(scrollElem => {
                  scrollElem.scrollLeft = 1000000;
                });
              }
            }, 500);
            this.ready = true;
          })
          .catch((error) => {
            if (error.response && error.response.data && error.response.data.errorCode == "patientNotSynchronized") {
              this.showAlertError({message: 'This patient has not been synced yet.'})
              this.list = [];
            } else {
              this.list = [];
            }
            setTimeout(() => {
              if (this.$el && this.$el.querySelectorAll) {
                this.$el.querySelectorAll(".b-virtual-scroller").forEach(scrollElem => {
                  scrollElem.scrollLeft = 1000000;
                });
              }
            }, 500);
            this.ready = true;
          });
      }
    },
    async submitVital() {
      const formData = {
        ...this.form,
        items: Object.keys(this.form.items).map(vitalSignId => ({
          vitalSignId: vitalSignId,
          value: this.form.items[vitalSignId]
        }))
      };
      formData.effectiveDate = this.form.effectiveDate
        ? formatDateTimeSql(this.form.effectiveDate)
        : null;
      if (this.formId) {
        await vitalRecordServer.update(this.formId, formData);
      } else {
        await vitalRecordServer.create(formData);
      }
      this.getVitals();
      this.showAlertSuccess();
    },
    addVital() {
      this.formId = null;
      this.form = {
        episodeOfCareId: this.eocId,
        items: {},
        effectiveDate: formatDateTimeMysql(this.getCurrentAdjustedDateTime())
      };
      this.ibw = { gender: null };
      this.$refs["form-modal"].show();
    },
    numberPairValue(position, vitalSignId) {
      let parts = this.form.items[vitalSignId]
        ? this.form.items[vitalSignId].split("/")
        : ["", ""];
      return parts[position];
    },
    setNumberPair(position, vitalSignId, newValue) {
      const parts = this.form.items[vitalSignId]
        ? this.form.items[vitalSignId].split("/")
        : ["", ""];
      parts[position] = newValue;
      this.form.items = { ...this.form.items, [vitalSignId]: parts.join("/") };
    },
    showVitalSignChart() {
      const values = this.viewVitalSign.values.filter(value => value.value);
      const chartData = values.map(value => parseFloat(value.value));
      const chartLabels = values.map(value => formatDateOnly(value.dateTime));
      this.viewVitalSignChartData = {
        labels: chartLabels,
        datasets: [
          {
            label: this.viewVitalSign.name,
            backgroundColor: "#4044E6",
            data: chartData,
            cubicInterpolationMode: "monotone"
          }
        ]
      };
    },
    exportExcel() {
      this.$refs.grid.exportExcel();
    },
    calculateBMI(m) {
      const bmi = (703 * m.weight) / (m.height * m.height);
      return Math.round(bmi * 100) / 100;
    },
    calculateIBW(m, g) {
      let base, multiplier;

      if (g == "m") {
        base = 50;
        multiplier = 2.3;
      } else if (g == "f") {
        base = 45.5;
        multiplier = 2.3;
      }

      const ibwKgs =
        m.height <= 60 ? base : base + (m.height - 60) * multiplier;
      const ibwPounds = ibwKgs * 2.205;

      return Math.round(ibwPounds * 100) / 100;
    },
    getMeasurements() {
      const height = this.form.items[this.heightVitalSign.id];
      const weight = this.form.items[this.weightVitalSign.id];

      if (!height || !weight) {
        return null;
      }

      const heightSegments = height.split("/");
      const inches =
        parseInt(heightSegments[0] || "0") * 12 +
        parseInt(heightSegments[1] || "0");

      return { height: inches, weight };
    },
    async getVitalsColumnsForEoCLocation() {
      this.currentVitalSignsIds = await catalogServer.getVitalsForLocation(
        this.eoc.locationId
      );
    },
    addOneDay(date) {
      var result = new Date(date);
      result.setDate(result.getDate() + 1);
      return result;
    },
    callWithTimeout(callback, timeout) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(callback, timeout);
    }
  },
  computed: {
    ...mapGetters("client", ["vitalSignsCurrentLocation"]),
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    ...mapState("layout", {
      selectedLocation: state => state.selectedLocation,
      isEMAREnabled: state => state.systemSetting?.emarEnabled
    }),
    ...mapState("client", {
      allVitalSigns: state => state.vitalSigns
    }),
    vitalSigns() {
      if (this.isEMAREnabled && this.list.length > 0) {
        const vitalSignsMap = new Map();
        for (let i = 0; i < this.list.length; i++) {
          const record = this.list[i];
          if (record.items && Array.isArray(record.items)) {
            for (let j = 0; j < record.items.length; j++) {
              const item = record.items[j];
              if (item && item.vitalSign && !vitalSignsMap.has(item.vitalSign.id)) {
                vitalSignsMap.set(item.vitalSign.id, item.vitalSign);
              }
            }
          }
        }
        return Array.from(vitalSignsMap.values());
      }
      return this.vitalSignsCurrentLocation.length === 0
        ? this.allVitalSigns.filter(vs => {
            return this.currentVitalSignsIds.some(cvs => cvs === vs.id);
          })
        : this.vitalSignsCurrentLocation;
    },
    vitalSignsNonAuto() {
      return this.vitalSigns.filter(
        vitalSign => vitalSign.type != "autocalculated"
      );
    },
    bmiVitalSign() {
      return this.vitalSigns.find(
        vitalSign =>
          vitalSign.name == "BMI" && vitalSign.type == "autocalculated"
      );
    },
    ibwVitalSign() {
      return this.vitalSigns.find(
        vitalSign =>
          vitalSign.name == "IBW" && vitalSign.type == "autocalculated"
      );
    },
    ibwPercentVitalSign() {
      return this.vitalSigns.find(
        vitalSign =>
          vitalSign.name == "IBW%" && vitalSign.type == "autocalculated"
      );
    },
    heightVitalSign() {
      return this.vitalSigns.find(
        vitalSign =>
          vitalSign.name == "Height" &&
          vitalSign.type == "numberPair" &&
          vitalSign.unit == "ft, in"
      );
    },
    weightVitalSign() {
      return this.vitalSigns.find(
        vitalSign =>
          vitalSign.name == "Weight" &&
          vitalSign.type == "number" &&
          vitalSign.unit == "lbs"
      );
    },
    bmiEnabled() {
      return !!this.bmiVitalSign;
    },
    bmiError() {
      return (
        this.bmiEnabled && !(!!this.heightVitalSign && !!this.weightVitalSign)
      );
    },
    columns() {
      let columns = [
        {
          text: "",
          field: "vitalSign",
          type: "template",
          width: "500px",
          editor: false,
          sortable: false,
          locked: true,
          template: data =>
            `<div class="d-flex flex-row justify-content-between align-items-center w-100"><div class="w-100 vital-sign-cell d-flex align-items-center">${
              data.record.data.icon
                ? `<img class="mr-2 ml-2" src="${data.record.data.icon}">`
                : `<img class="mr-2 ml-2" width="20" height="20" src="${
                    this.isStandalone()
                      ? `/icons/heart.svg`
                      : `/desktop/icons/heart.svg`
                  }">`
            }${
              data.record.data.name
            } ${
              data.record.data.unit ? `(${data.record.data.unit})` : ""
            }<img class="b-action-item view-vital ml-4" data-id="${
              data.record.data.id
            }" src="${
              this.isStandalone()
                ? `/icons/loupe.svg`
                : `/desktop/icons/loupe.svg`
            }"></div><span id="vitalSign${data.record.data.id}"></span></div>`
        }
      ];
      for (let index = 0; index < this.list.length; index++) {
        const itemValue = this.list[index];
        const items = {};
        if (itemValue.items && Array.isArray(itemValue.items)) {
          for (let index2 = 0; index2 < itemValue.items.length; index2++) {
            const item = itemValue.items[index2];
            if (item && item.vitalSign && item.vitalSign.id) {
              items[item.vitalSign.id] = item.value;
            }
          }
        }
        columns.push({
          text: "",
          field: itemValue.id,
          type: "template",
          width: "250px",
          editor: false,
          sortable: false,
          headerRenderer: () => {
            return `<div class="d-flex flex-row justify-content-between align-items-center header-custom" data-id="${
              itemValue.id
            }"><label>${formatDate(itemValue.effectiveDate)} <br>by ${
              itemValue.user ? this.formatUser(itemValue.user) : ""
            }</label>
            ${ this.isEMAREnabled ? "" : 
             `
              <div class="header-actions"><img class="b-action-item edit-vital" data-id="${
                itemValue.id
              }" src="${
                this.isStandalone()
                  ? `/icons/edit.svg`
                  : `/desktop/icons/edit.svg`
              }"><img class="b-action-item delete-vital ml-2" data-id="${
                itemValue.id
              }" src="${
                this.isStandalone()
                  ? `/icons/delete.svg`
                  : `/desktop/icons/delete.svg`
              }"></div>
             `
            }
            </div>`;
          },
          template: data => {
            const vitalSignId = data.record.data.id;
            const value = items[vitalSignId];
            return value !== undefined && value !== null ? value : "";
          }
        });
      }
      if (this.list.length == 0) {
        columns.push({
          text: "",
          field: "",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: ({ record }) =>
            record.parentIndex == Math.floor(this.vitalSigns.length / 2)
              ? "No records to display"
              : "",
          align: "center"
        });
      } else {
        columns.push({
          text: "",
          field: "",
          type: "template",
          flex: 1,
          minWidth: 0,
          editor: false,
          sortable: false,
          template: () => "",
          align: "center"
        });
      }
      return columns;
    },
    viewColumns() {
      let columns = [
        {
          text: "Date and time",
          field: "dateTime",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: data => formatDate(data.record.data.dateTime)
        },
        {
          text: "Result",
          field: "value",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: data => data.record.data.value
        }
      ];
      return columns;
    },
    excelExporterFeature() {
      let columns = [
        {
          text: "",
          field: "vitalSign",
          width: 400,
          renderer(data) {
            return `${data.record.data.name} ${ data.record.data.unit? `(${data.record.data.unit})`: "" }`;
          }
        }
      ];
      for (let index = 0; index < this.list.length; index++) {
        const itemValue = this.list[index];
        const items = {};
        for (let index = 0; index < itemValue.items.length; index++) {
          const item = itemValue.items[index];
          items[item.vitalSign.id] = item.value;
        }
        columns.push({
          text: `${formatDate(itemValue.effectiveDate)} by ${
            itemValue.user ? this.formatUser(itemValue.user) : ""
          }`,
          field: itemValue.id,
          width: 400,
          renderer(data) {
            return items[data.record.data.id] ? items[data.record.data.id] : "";
          }
        });
      }

      return {
        zipcelx,
        filename: `${this.eoc.mrnEocn}_${this.client.firstName}_${this.client.middleName}_${this.client.lastName}_Vitals`,
        exporterConfig: {
          columns
        }
      };
    }
  },
  watch: {
    currentView: function() {
      this.getVitals();
    },
    showing: {
      handler: function(newValue) {
        if (newValue) {
          this.fetchData();
        }
      },
      immediate: true
    },
    "form.items": {
      handler() {
        if (!this.bmiEnabled || this.bmiError) {
          return;
        }

        const m = this.getMeasurements();
        if (!m) {
          return;
        }

        this.form.items[this.bmiVitalSign.id] = this.calculateBMI(m).toString();

        if (!this.ibw.gender) {
          return;
        }

        const total = this.calculateIBW(m, this.ibw.gender);
        const perc = (m.weight * 100) / total;

        this.form.items[this.ibwVitalSign.id] = total.toString();
        this.form.items[this.ibwPercentVitalSign.id] = (
          Math.round(perc * 100) / 100
        ).toString();
      },
      deep: true
    },
    "ibw.gender": function(newValue, oldValue) {
      if (!newValue || newValue == oldValue) {
        return;
      }

      const m = this.getMeasurements();
      if (!m) {
        return;
      }

      const total = this.calculateIBW(m, newValue);
      const perc = (m.weight * 100) / total;

      this.form.items[this.ibwVitalSign.id] = total.toString();
      this.form.items[this.ibwPercentVitalSign.id] = (
        Math.round(perc * 100) / 100
      ).toString();
    },
    datetimeFromEmar: {
      handler(_, oldValue) {
        if (!oldValue) {
          return;
        }
        this.callWithTimeout(() => {
          this.getVitals();
        }, 1000);
      },
      immediate: true
    },
    datetimeToEmar: {
      handler(_, oldValue) {
        if (!oldValue) {
          return;
        }
        this.callWithTimeout(() => {
          this.getVitals();
        }, 1000);
      },
      immediate: true
    }
  }
};
</script>

<style>
.header-actions {
  display: none;
  position: absolute;
  top: 2px;
  right: 0;
  width: 40px;
}
.header-custom {
  position: relative;
}
.header-custom:hover .header-actions {
  display: initial;
}
.view-vital {
  visibility: hidden;
}
.vital-sign-cell:hover .view-vital {
  visibility: initial;
}
.client-vitals .b-grid-header {
  border-top: 1px solid transparent;
  border-left: 1px solid transparent;
  border-right: 1px solid transparent !important;
}
.client-vitals .grid-selected-column.b-grid-header {
  border-top: 1px solid #4044e6;
}
.client-vitals .grid-selected-column {
  background-color: white;
  border-left: 1px solid #4044e6 !important;
  border-right: 1px solid #4044e6 !important;
}
.client-vitals .grid-selected-column:hover {
  background-color: white !important;
}
.client-vitals
  .b-gridbase:not(.b-moving-splitter)
  .b-grid-row.b-hover
  .b-grid-cell {
  background-color: white !important;
}
.client-vitals .b-grid-row {
  border: 1px solid transparent;
}
.client-vitals .b-grid-subgrid .b-grid-row.b-hover {
  border-top: 1px solid #4044e6;
  border-bottom: 1px solid #4044e6;
  border-right: 1px solid #4044e6;
}
.client-vitals .b-grid-subgrid:first-child .b-grid-row.b-hover {
  border-right: 1px solid transparent !important;
  border-left: 1px solid #4044e6;
}
.client-vitals .b-grid-row:last-child .b-grid-cell {
  border-bottom: 1px solid transparent;
}
.client-vitals .b-grid-row:last-child .grid-selected-column {
  border-bottom: 1px solid #4044e6;
}
.client-vitals .b-grid-splitter {
  display: none;
}
.vital-view-container .b-grid-container .b-container {
  max-height: calc(100vh - 627px) !important;
}
.fa-icon-vital {
  font-size: 15px !important;
  margin: 0px 11px;
}
</style>
